import Tooltip from "components/atoms/Tooltip";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import MUITheme from "components/MUITheme";
import { Header, Paragraph } from "components/atoms/Typography";
import { DataSource, Table } from "components/molecules/Table";

import { getDisplayInfo } from "utils/formatting";
import useBuildingsPageState from "recoilStore/useBuildingsPageState";
import DataCoverageLine from "components/molecules/DataCoverageLine";
import { PropertyBasedEmissionIntensityMap } from "components/organisms/Charts";

import useTableState from "recoilStore/Table/useTableState";
import { TABLE_IDS } from "utils/constants";
import SearchBar from "components/organisms/SearchBar/SearchBar";

import "./Properties.scss";
import { getPropertyDataCoverageMap, getPropertyDataCoverageSortValue } from "./Helper/PortfolioPage.helper";

interface PropertiesProps {
    properties: Property[];
    selectedProperties: Property[];
    setSelectedProperties: React.Dispatch<React.SetStateAction<Property[]>>;
}

function Properties({ properties, selectedProperties, setSelectedProperties }: PropertiesProps) {
    const navigate = useNavigate();
    const { pageState } = useBuildingsPageState();

    const { setResetSearchTrigger, setSearchBarText, setTablePage } = useTableState(TABLE_IDS.PROPERTIES);

    const resetSearchBar = useCallback(() => {
        setResetSearchTrigger();
        setSearchBarText("");
        setTablePage(0);
    }, [setResetSearchTrigger, setSearchBarText, setTablePage]);

    const onRowClick = useCallback(
        (row: DataSource) => {
            const { propertyName } = row;
            resetSearchBar();
            const propertyNameEncoded = encodeURIComponent(propertyName);
            navigate(`/reports/baseline?propertyName=${propertyNameEncoded}`);
        },
        [resetSearchBar, navigate]
    );

    const dataSource: DataSource[] = useMemo(
        () =>
            properties.map((property) => ({
                ...property,
                streetAddress: getStreetAddressLabel(property.buildingModels),
                buildingCount: property.buildingModels.length,
                propertyArchetype: getPropertyTypeDisplayLabel(property.buildingModels),
                dataCoverages: <DataCoverageLine map={getPropertyDataCoverageMap(property.buildingModels)} />,
                dataCoverageSortValue: getPropertyDataCoverageSortValue(property.buildingModels),
                tags: getTagsDisplayValue(property.buildingModels),
                key: property.propertyName,
            })),
        [properties]
    );

    const columns = useMemo(
        () =>
            tableColumns.map((columnKey) => {
                const { formatFunction, humanReadable, unit, tooltip } =
                    dataSource[0] && typeof dataSource[0][columnKey] === "number"
                        ? getDisplayInfo(columnKey, dataSource.map((d) => d[columnKey]) as number[])
                        : getDisplayInfo(columnKey);

                const baseColumn = {
                    title: `${humanReadable} ${unit && `(${unit})`}`,
                    dataIndex: columnKey,
                    key: columnKey,
                    render: (value: any, index: number, record: DataSource) => {
                        if (columnKey === "streetAddress") {
                            if (!record?.buildingModels) {
                                return <span>No address</span>;
                            }
                            return <Address propertyBuildingModels={record.buildingModels} />;
                        }
                        if (columnKey === "propertyArchetype") {
                            if (!record?.buildingModels) {
                                return <span>No type</span>;
                            }
                            return <PropertyTypeDisplayLabel propertyBuildingModels={record.buildingModels} />;
                        }

                        if (value === null || value === undefined) return "";
                        return formatFunction(value);
                    },
                    tooltip,
                };

                // customSort for dataCoverages column
                if (columnKey === "dataCoverages") {
                    return {
                        ...baseColumn,
                        customSort: (order: "asc" | "desc") => (a: DataSource, b: DataSource) => {
                            const aValue = a.dataCoverageSortValue as number;
                            const bValue = b.dataCoverageSortValue as number;

                            if (aValue === bValue) {
                                return order === "desc"
                                    ? b.propertyName.localeCompare(a.propertyName)
                                    : a.propertyName.localeCompare(b.propertyName);
                            }

                            const result = aValue - bValue;
                            return order === "desc" ? -result : result;
                        },
                    };
                }

                // customSort for dataCoverages column
                if (columnKey === "dataCoverages") {
                    const customSort = (order: "asc" | "desc") => (a: DataSource, b: DataSource) => {
                        const aValue = a.dataCoverageSortValue as number;
                        const bValue = b.dataCoverageSortValue as number;

                        if (aValue === bValue) {
                            return order === "desc"
                                ? b.propertyName.localeCompare(a.propertyName)
                                : a.propertyName.localeCompare(b.propertyName);
                        }

                        const result = aValue - bValue;
                        return order === "desc" ? -result : result;
                    };

                    const columnWithCustomSort = {
                        ...baseColumn,
                        customSort,
                    };
                    return columnWithCustomSort;
                }

                return baseColumn;
            }),
        [dataSource]
    );

    return (
        <div className="properties-page">
            <div className="view-body">
                {pageState.view === "table" && (
                    <div>
                        <SearchBar tableId={TABLE_IDS.PROPERTIES} />

                        <Table
                            columns={columns}
                            dataSource={dataSource}
                            onRowClick={onRowClick}
                            selectedRows={selectedProperties as any} // TODO: remove any
                            setSelectedRows={setSelectedProperties as any}
                            parentComponentWithSearchBarId={TABLE_IDS.PROPERTIES}
                        />
                    </div>
                )}
                {pageState.view === "map" && <PropertyBasedEmissionIntensityMap properties={properties} />}
            </div>
        </div>
    );
}

const MULTIPLE_LABEL = "Multiple";

interface AddressProps {
    propertyBuildingModels: PropertyBuildingModel[];
}

function Address({ propertyBuildingModels }: AddressProps) {
    const label = getStreetAddressLabel(propertyBuildingModels);

    if (label !== MULTIPLE_LABEL) return <span>{label}</span>;

    return (
        <Tooltip tooltipBody={<AddressTooltip propertyBuildingModels={propertyBuildingModels} />}>
            <span> {MULTIPLE_LABEL} </span>
        </Tooltip>
    );
}

const getStreetAddressLabel = (propertyBuildingModels: PropertyBuildingModel[]) => {
    if (propertyBuildingModels.length < 1) return "";

    const address = propertyBuildingModels[0].location.streetAddress;

    const allAddressAreSame = propertyBuildingModels.every(({ location }) => location.streetAddress === address);

    if (allAddressAreSame) return address;
    return MULTIPLE_LABEL;
};

function AddressTooltip({ propertyBuildingModels }: AddressProps) {
    const addresses = propertyBuildingModels
        .map(({ location, buildingName }) => ({
            address: location.streetAddress,
            name: buildingName,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const sliced = addresses.slice(0, 4);

    return (
        <div className="property-address-tooltip">
            <Header size="x-small" style={{ color: "var(--audette-gray-500)" }}>
                Address
            </Header>
            {sliced.map(({ name, address }) => (
                <div key={address}>
                    <Header size="x-small">{name}</Header>
                    <Paragraph size="small" key={address}>
                        {address}
                    </Paragraph>
                </div>
            ))}
        </div>
    );
}

interface PropertyTypeDisplayLabelProps {
    propertyBuildingModels: PropertyBuildingModel[];
}

function PropertyTypeDisplayLabel({ propertyBuildingModels }: PropertyTypeDisplayLabelProps) {
    const label = getPropertyTypeDisplayLabel(propertyBuildingModels);

    if (label !== MULTIPLE_LABEL) return <span>{label}</span>;
    return (
        <MUITheme>
            <Tooltip tooltipBody={<PropertyTypeDisplayLabelTooltip propertyBuildingModels={propertyBuildingModels} />}>
                <span> {MULTIPLE_LABEL} </span>
            </Tooltip>
        </MUITheme>
    );
}

const getPropertyTypeDisplayLabel = (propertyBuildingModels: PropertyBuildingModel[]) => {
    if (propertyBuildingModels.length < 1) return "";

    const { formatFunction } = getDisplayInfo("propertyArchetype");

    const type = propertyBuildingModels[0].buildingArchetype;

    const allTypesAreSame = propertyBuildingModels.every(({ buildingArchetype }) => buildingArchetype === type);

    if (allTypesAreSame) return formatFunction(type);
    return MULTIPLE_LABEL;
};

function PropertyTypeDisplayLabelTooltip({ propertyBuildingModels }: PropertyTypeDisplayLabelProps) {
    const types = propertyBuildingModels
        .map(({ buildingArchetype, buildingName }) => ({
            type: buildingArchetype,
            name: buildingName,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const sliced = types.slice(0, 4);

    const { formatFunction } = getDisplayInfo("propertyArchetype");

    return (
        <div className="property-address-tooltip">
            <Header size="x-small" style={{ color: "var(--audette-gray-500)" }}>
                Building type
            </Header>
            {sliced.map(({ name, type }) => (
                <div key={type}>
                    <Header size="x-small">{name}</Header>
                    <Paragraph size="small" key={type}>
                        {formatFunction(type)}
                    </Paragraph>
                </div>
            ))}
        </div>
    );
}

const getTagsDisplayValue = (propertyBuildingModels: PropertyBuildingModel[]) => {
    const tags = propertyBuildingModels.flatMap(({ tags }) => tags);
    const uniqueTags = new Set([...tags]);

    return Array.from(uniqueTags);
};

const tableColumns = [
    "propertyName",
    "streetAddress",
    "propertyArchetype",
    "dataCoverages",
    "tags",
    "fundName",
    "buildingCount",
    "grossFloorArea",
    "carbonEmissionReductionPotential",
    "averageMarginalAbatementCost",
    "netPresentValueIntensity",
    "annualUtilityCostIntensityCurrent",
    "annualCarbonPolicyCostIntensityCurrent",
    "annualCarbonEmissionIntensityCurrent",
    "annualEnergyUseIntensityCurrent",
];

export default Properties;
